import { useSeoMeta, useHead } from '@unhead/react';
import { Link } from 'react-router-dom';
import {Calendar, ChevronRight, Clock, Tag} from 'lucide-react';
import { SiteShell } from '@/components/SiteShell';
import { PageHero } from '@/components/PageHero';
import type { BlogPostConfig } from '@/lib/blogPosts';
import { blogPostConfigs } from '@/lib/blogPosts';

type BlogPostProps = {
  config: BlogPostConfig;
};

export function BlogPost({ config }: BlogPostProps) {
  const related = blogPostConfigs
    .filter((p) => p.slug !== config.slug)
    .slice(0, 3);

  useSeoMeta({
    title: `${config.title} | VIP House Movers Blog`,
    description: config.metaDescription,
    ogTitle: `${config.title} | VIP House Movers Blog`,
    ogDescription: config.metaDescription,
    ogImage: config.heroImage,
    ogUrl: `https://www.viphousemovers.com${config.path}`,
    ogType: 'article',
    twitterCard: 'summary_large_image',
    twitterTitle: `${config.title} | VIP House Movers Blog`,
    twitterDescription: config.metaDescription,
    twitterImage: config.heroImage,
  });
  useHead({
    link: [{ rel: 'canonical', href: `https://www.viphousemovers.com${config.path}` }],
    meta: [
      {
        name: 'keywords',
        content: `${config.keywords.join(', ')}, moving tips, removals advice, moving guide, house removals blog`,
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: config.title,
          image: config.heroImage,
          datePublished: config.date,
          dateModified: config.date,
          author: { '@type': 'Organization', name: 'VIP House Movers', url: 'https://www.viphousemovers.com' },
          publisher: {
            '@type': 'Organization',
            name: 'VIP House Movers',
            logo: { '@type': 'ImageObject', url: 'https://www.viphousemovers.com/logo.png' },
            url: 'https://www.viphousemovers.com',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://www.viphousemovers.com${config.path}`,
          },
          description: config.metaDescription,
          keywords: config.keywords.join(', '),
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://www.viphousemovers.com',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: 'https://www.viphousemovers.com/blog',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: config.title,
              item: `https://www.viphousemovers.com${config.path}`,
            },
          ],
        }),
      },
    ],
  });

  return (
    <SiteShell activePath="/blog" fixedNav>
      <PageHero
        eyebrow={config.category}
        title={config.title}
        description={config.metaDescription}
        align="left"
        backgroundImage={config.heroImage}
      />

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Breadcrumb and meta */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-8">
          <Link to="/blog" className="hover:text-[#D4AF37] font-semibold">
            Blog
          </Link>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="text-gray-900 font-semibold line-clamp-1">{config.title}</span>
        </div>

        <div className="flex flex-wrap items-center gap-5 text-sm text-gray-600 mb-10 pb-8 border-b border-gray-200">
          <span className="inline-flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#D4AF37]" />
            {config.date}
          </span>
          <span className="inline-flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#D4AF37]" />
            {config.readTime}
          </span>
          <span className="inline-flex items-center gap-2">
            <Tag className="w-4 h-4 text-[#D4AF37]" />
            {config.category}
          </span>
        </div>

        {/* Hero image */}
        <figure className="mb-12 rounded-2xl overflow-hidden shadow-lg">
          <img
            src={config.heroImage}
            alt={config.heroImageAlt}
            className="w-full h-64 md:h-96 object-cover"
            loading="lazy"
          />
        </figure>

        {/* Intro */}
        <div className="prose-vip space-y-6 text-lg text-gray-700 leading-relaxed mb-12">
          {config.intro.map((p, i) => (
            <p key={i} className="text-xl font-light leading-relaxed">
              {p}
            </p>
          ))}
        </div>

        {/* Sections */}
        <div className="space-y-14">
          {config.sections.map((section, i) => (
            <section key={i}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                {section.heading}
              </h2>

              {section.paragraphs?.map((p, j) => (
                <p key={j} className="text-lg text-gray-700 leading-relaxed mb-4">
                  {p}
                </p>
              ))}

              {section.bullets && section.bullets.length > 0 ? (
                <ul className="mt-4 space-y-3 bg-gray-50 border border-gray-100 rounded-2xl p-6">
                  {section.bullets.map((b, k) => (
                    <li key={k} className="flex items-start gap-3 text-gray-800">
                      <span className="mt-2 h-2 w-2 rounded-full bg-[#D4AF37] shrink-0" />
                      <span className="leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              {section.image ? (
                <figure className="mt-8 rounded-2xl overflow-hidden shadow-md">
                  <img
                    src={section.image.src}
                    alt={section.image.alt}
                    className="w-full h-56 md:h-80 object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  {section.image.caption ? (
                    <figcaption className="text-sm text-gray-500 italic mt-3 text-center">
                      {section.image.caption}
                    </figcaption>
                  ) : null}
                </figure>
              ) : null}
            </section>
          ))}
        </div>

        {/* Checklist */}
        {config.checklist ? (
          <section className="mt-16 bg-[#111827] text-white rounded-2xl p-8 md:p-10">
            <p className="text-[#D4AF37] font-semibold uppercase tracking-wider text-sm mb-2">
              Quick Reference
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">{config.checklist.title}</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {config.checklist.items.map((item, i) => (
                <li key={i} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                  <span className="mt-1 inline-flex w-5 h-5 items-center justify-center rounded-md bg-[#D4AF37] text-[#111827] text-xs font-bold">
                    ✓
                  </span>
                  <span className="text-gray-100">{item}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* FAQs */}
        {config.faqs && config.faqs.length > 0 ? (
          <section className="mt-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {config.faqs.map((f, i) => (
                <details
                  key={i}
                  className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-[#D4AF37]/40 transition-colors"
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-semibold text-lg text-gray-900">
                    <span>{f.question}</span>
                    <ChevronRight className="w-5 h-5 text-[#D4AF37] transition-transform group-open:rotate-90 shrink-0 mt-1" />
                  </summary>
                  <p className="mt-4 text-gray-700 leading-relaxed">{f.answer}</p>
                </details>
              ))}
            </div>
          </section>
        ) : null}

        {/* Conclusion */}
        <section className="mt-16 border-t border-gray-200 pt-10">
          {config.conclusion.map((p, i) => (
            <p key={i} className="text-lg text-gray-700 leading-relaxed mb-4">
              {p}
            </p>
          ))}
        </section>

        {/* CTA */}
        <section className="mt-12 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 via-white to-white p-8 md:p-10 text-center">
          <p className="text-[#8a7118] font-semibold uppercase tracking-wider text-sm mb-2">
            VIP House Movers
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Planning a move across the Midlands?
          </h2>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            Speak to our team for a free, no-obligation home survey and a fixed written quote.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/quote"
              className="bg-[#D4AF37] text-[#111827] font-semibold px-6 py-3 rounded-lg hover:bg-[#c9a332] transition-colors"
            >
              Get a Free Quote
            </Link>
            <Link
              to="/contact"
              className="border border-gray-300 text-gray-900 font-semibold px-6 py-3 rounded-lg hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              Contact VIP House Movers
            </Link>
          </div>
        </section>

        {/* Keywords for on-page SEO */}
        <div className="mt-12 flex flex-wrap gap-2">
          {config.keywords.map((k) => (
            <span
              key={k}
              className="text-xs font-semibold uppercase tracking-wider text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-3 py-1"
            >
              #{k}
            </span>
          ))}
        </div>
      </article>

      {/* Related posts */}
      {related.length > 0 ? (
        <section className="bg-white border-t border-gray-200 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Keep Reading</h2>
              <Link to="/blog" className="text-[#D4AF37] font-semibold hover:underline">
                View all articles &rarr;
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((post) => (
                <Link
                  key={post.slug}
                  to={post.path}
                  className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={post.heroImage}
                      alt={post.heroImageAlt}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="p-5">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#D4AF37] mb-2">
                      {post.category} • {post.readTime}
                    </p>
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{post.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </SiteShell>
  );
}

